import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Alert,
  AlertTitle,
  Box,
  Chip,
  Stack,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { usersApi } from '@api/endpoints/users'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import type {
  AdminCheckInCandidateDto,
  AdminAwardCheckInResultDto,
} from '@api/types/checkInAward'

interface AwardCheckInDialogProps {
  open: boolean
  onClose: () => void
  userId: string
  userLabel: string
}

/**
 * Grants a user a completion for a visit that really happened but which the system never recorded.
 *
 * The screen is built around one hazard: an award against content that fails the achievement
 * credit gate writes a real session and earns nothing. That verdict is shown in the picker, again
 * as a blocking warning once an adventure is chosen, and once more in the result — because the
 * whole reason to reach for this tool is the achievements.
 */
export default function AwardCheckInDialog({
  open,
  onClose,
  userId,
  userLabel,
}: AwardCheckInDialogProps) {
  const mobileDialogProps = useMobileDialogProps()
  const [options, setOptions] = useState<AdminCheckInCandidateDto[]>([])
  const [searchText, setSearchText] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [selected, setSelected] = useState<AdminCheckInCandidateDto | null>(null)

  const [occurredAt, setOccurredAt] = useState<Date | null>(new Date())
  const [reason, setReason] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AdminAwardCheckInResultDto | null>(null)

  // Keeps a slow response from overwriting the results of a later keystroke.
  const searchSeq = useRef(0)

  const runSearch = useCallback(
    async (term: string) => {
      const seq = ++searchSeq.current
      setSearching(true)
      try {
        const found = await usersApi.getCheckInCandidates(userId, term)
        if (seq === searchSeq.current) {
          setOptions(found)
          setSearchError(null)
        }
      } catch (err: unknown) {
        // Never swallow this into an empty list. A failing search and a search that genuinely
        // matches nothing look identical in an Autocomplete, and reading "no adventures match" as
        // an empty database rather than a broken request costs an afternoon.
        if (seq === searchSeq.current) {
          setOptions([])
          const status = (err as { response?: { status?: number } })?.response?.status
          setSearchError(
            status
              ? `Adventure search failed (HTTP ${status}).`
              : 'Adventure search failed — the request did not complete.',
          )
        }
      } finally {
        if (seq === searchSeq.current) setSearching(false)
      }
    },
    [userId],
  )

  useEffect(() => {
    if (!open) return
    const handle = setTimeout(() => runSearch(searchText), 300)
    return () => clearTimeout(handle)
  }, [open, searchText, runSearch])

  useEffect(() => {
    if (open) return
    // Reset on close so the next user does not inherit this one's selection.
    setSearchText('')
    setSearchError(null)
    setSelected(null)
    setOccurredAt(new Date())
    setReason('')
    setAcknowledged(false)
    setError(null)
    setResult(null)
  }, [open])

  // A fresh selection has not been acknowledged yet.
  useEffect(() => setAcknowledged(false), [selected])

  const alreadyCompleted = Boolean(selected?.alreadyCompletedAt)
  const needsAcknowledgement = Boolean(selected && !selected.earnsAchievementCredit)
  const inFuture = Boolean(occurredAt && occurredAt.getTime() > Date.now())

  const canSubmit =
    !!selected &&
    !!occurredAt &&
    !inFuture &&
    !alreadyCompleted &&
    reason.trim().length > 0 &&
    (!needsAcknowledgement || acknowledged) &&
    !submitting

  const handleAward = async () => {
    if (!selected || !occurredAt) return
    setSubmitting(true)
    setError(null)
    try {
      const awarded = await usersApi.awardCheckIn(userId, {
        adventureId: selected.adventureId,
        occurredAt: occurredAt.toISOString(),
        reason: reason.trim(),
        acknowledgeNoCredit: acknowledged,
      })
      setResult(awarded)
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'The award could not be recorded.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth {...mobileDialogProps}>
      <DialogTitle>Award a missed check-in</DialogTitle>

      {result ? (
        <>
          <DialogContent>
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>Recorded</AlertTitle>
              {userLabel} now has a completion for <strong>{result.adventureTitle}</strong> dated{' '}
              {new Date(result.occurredAt).toLocaleString()}.
            </Alert>

            {!result.earnedAchievementCredit && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No achievement credit was earned, because {result.creditRefusedReason}. The visit is
                recorded, but nothing advanced.
              </Alert>
            )}

            {result.achievementsAdvanced.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  Achievements advanced
                </Typography>
                <List dense>
                  {result.achievementsAdvanced.map((a) => (
                    <ListItem key={a.achievementId} disableGutters>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {a.achievementName}
                            {a.newlyCompleted && (
                              <Chip
                                size="small"
                                color="success"
                                icon={<EmojiEventsIcon />}
                                label="Earned"
                              />
                            )}
                          </Box>
                        }
                        secondary={`${a.collectionName} — ${a.completedBefore} → ${a.completedAfter} of ${a.required}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {result.collectionsAdvanced.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  Collections advanced
                </Typography>
                <List dense>
                  {result.collectionsAdvanced.map((c) => (
                    <ListItem key={c.collectionId} disableGutters>
                      <ListItemText
                        primary={c.collectionName}
                        secondary={
                          `${c.completedAchievementsBefore} → ${c.completedAchievementsAfter} of ${c.totalAchievements} achievements` +
                          (c.badgeAwarded ? ` — badge awarded` : '')
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {result.earnedAchievementCredit &&
              result.achievementsAdvanced.length === 0 && (
                <Alert severity="info">
                  This adventure is not part of any achievement, so the completion stands on its own.
                </Alert>
              )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} variant="contained">
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Records a completion for {userLabel} as though the visit had been detected at the time
              it happened. Achievement and collection progress move exactly as they would have.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {searchError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {searchError}
              </Alert>
            )}

            <Autocomplete
              sx={{ mb: 2 }}
              options={options}
              value={selected}
              loading={searching}
              onChange={(_, value) => setSelected(value)}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') setSearchText(value)
              }}
              getOptionLabel={(option) => option.title}
              isOptionEqualToValue={(a, b) => a.adventureId === b.adventureId}
              filterOptions={(x) => x}
              noOptionsText={
                searching ? 'Searching…' : searchError ?? 'No adventures match'
              }
              renderOption={(props, option) => (
                <li {...props} key={option.adventureId}>
                  <Box sx={{ py: 0.5, width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{option.title}</Typography>
                      {!option.earnsAchievementCredit && (
                        <Chip size="small" color="warning" label="No credit" />
                      )}
                      {option.alreadyCompletedAt && (
                        <Chip size="small" color="default" label="Already completed" />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {[
                        option.categoryName,
                        option.achievements.length > 0
                          ? `${option.achievements.length} achievement${option.achievements.length === 1 ? '' : 's'}`
                          : 'no achievements',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Adventure"
                  placeholder="Search by title"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searching ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {alreadyCompleted && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {userLabel} already completed this on{' '}
                {new Date(selected!.alreadyCompletedAt!).toLocaleDateString()}. A second award would
                earn nothing new.
              </Alert>
            )}

            {needsAcknowledgement && !alreadyCompleted && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>This will not earn any achievement</AlertTitle>
                Credit is refused because {selected!.creditRefusedReason}. The visit would be
                recorded, but no achievement or collection progress moves.
                <FormControlLabel
                  sx={{ mt: 1, display: 'block' }}
                  control={
                    <Checkbox
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                    />
                  }
                  label="Record the visit anyway"
                />
              </Alert>
            )}

            {selected && selected.achievements.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                {selected.achievements.map((name) => (
                  <Chip key={name} size="small" variant="outlined" label={name} />
                ))}
              </Stack>
            )}

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="When the visit happened"
                value={occurredAt}
                onChange={setOccurredAt}
                disableFuture
                sx={{ width: '100%', mb: 2 }}
                slotProps={{
                  textField: {
                    error: inFuture,
                    helperText: inFuture ? 'The visit cannot be in the future.' : ' ',
                  },
                }}
              />
            </LocalizationProvider>

            <TextField
              label="Reason"
              placeholder="Why this is being awarded — recorded against the user's audit history"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleAward} variant="contained" disabled={!canSubmit}>
              {submitting ? 'Awarding…' : 'Award check-in'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
